import { global } from '../../core/GlobalState';
function formatNumber(value, precision = 1) {
    const factor = Math.pow(10, precision);
    return Math.round(value * factor) / factor;
}
export class DiagnosticsService {
    constructor() {
        var _a;
        this.memoryInfo = null;
        this.storageStats = null;
        this.storageDirty = false;
        this.storagePersisted = null;
        this.storageLastRequest = 0;
        this.storageThrottleMs = 15000;
        this.storagePromise = null;
        this.recentWindowMs = 5000;
        this.networkDirty = false;
        this.networkStats = {
            activeRequests: 0,
            startedRequests: 0,
            completedRequests: 0,
            lastTransferBytes: 0,
            lastDurationMs: 0,
            lastUpdated: 0,
            recentBytesPerSecond: 0,
            recentRequestsPerSecond: 0
        };
        this.recentNetworkEvents = [];
        this.recentByteTotal = 0;
        this.fetchPatched = false;
        this.xhrPatched = false;
        this.systemDiagnostics = {
            summary: '—',
            gpu: '—',
            userAgent: navigator.userAgent,
            platform: (_a = navigator.platform) !== null && _a !== void 0 ? _a : '—'
        };
        this.captureMemoryInfo();
        this.computeSystemDiagnostics();
        this.refreshStorageEstimate(true);
        this.computeStoragePersistence();
        this.setupNetworkMonitoring();
    }
    static getInstance() {
        if (!DiagnosticsService.instance) {
            DiagnosticsService.instance = new DiagnosticsService();
        }
        return DiagnosticsService.instance;
    }
    getSystemDiagnostics() {
        if (global && global.renderer && !this.systemDiagnostics.summary.includes('Aniso')) {
            this.computeSystemDiagnostics();
        }
        return this.systemDiagnostics;
    }
    getMemoryInfo() {
        this.captureMemoryInfo();
        return this.memoryInfo;
    }
    refreshStorageEstimate(force = false) {
        const storage = navigator.storage;
        if (!storage || typeof storage.estimate !== 'function') {
            return;
        }
        const now = performance.now();
        if (!force && now - this.storageLastRequest < this.storageThrottleMs && this.storagePromise) {
            return this.storagePromise;
        }
        this.storageLastRequest = now;
        this.storagePromise = storage.estimate().then(estimate => {
            var _a, _b;
            this.storageStats = {
                usage: (_a = estimate.usage) !== null && _a !== void 0 ? _a : 0,
                quota: (_b = estimate.quota) !== null && _b !== void 0 ? _b : 0,
                persisted: this.storagePersisted,
                timestamp: performance.now()
            };
            this.storageDirty = true;
        }).catch(() => {
            this.storageStats = null;
            this.storageDirty = true;
        }).finally(() => {
            this.storagePromise = null;
        });
        return this.storagePromise;
    }
    getStorageStats() {
        return this.storageStats;
    }
    consumeStorageDirtyFlag() {
        if (this.storageDirty) {
            this.storageDirty = false;
            return true;
        }
        return false;
    }
    getNetworkSnapshot() {
        this.trimRecentNetworkEvents(performance.now());
        return Object.assign({}, this.networkStats);
    }
    consumeNetworkDirtyFlag() {
        if (this.networkDirty) {
            this.networkDirty = false;
            return true;
        }
        return false;
    }
    captureMemoryInfo() {
        const perf = performance;
        if (!perf.memory) {
            this.memoryInfo = null;
            return;
        }
        this.memoryInfo = {
            usedJSHeapSize: perf.memory.usedJSHeapSize,
            totalJSHeapSize: perf.memory.totalJSHeapSize,
            jsHeapSizeLimit: perf.memory.jsHeapSizeLimit
        };
    }
    computeSystemDiagnostics() {
        var _a, _b, _c, _d, _e;
        const nav = navigator;
        const cores = typeof nav.hardwareConcurrency === 'number' ? nav.hardwareConcurrency : 0;
        const deviceMemory = typeof nav.deviceMemory === 'number' ? nav.deviceMemory : 0;
        const dpr = typeof window.devicePixelRatio === 'number' ? window.devicePixelRatio : 1;
        const userAgentData = (_b = (_a = nav.userAgentData) === null || _a === void 0 ? void 0 : _a.brands) === null || _b === void 0 ? void 0 : _b.map(brand => `${brand.brand} ${brand.version}`).join(', ');
        const summaryParts = [];
        summaryParts.push(`Cores: ${cores || '—'}`);
        summaryParts.push(`RAM: ${deviceMemory ? `${formatNumber(deviceMemory, 1)} GB` : '—'}`);
        summaryParts.push(`DPR: ${formatNumber(dpr, 2)}`);
        if (global && global.renderer) {
            const anisotropy = global.renderer.capabilities.getMaxAnisotropy();
            if (anisotropy) {
                summaryParts.push(`Aniso: ${anisotropy}`);
            }
        }
        this.systemDiagnostics.summary = summaryParts.join(' | ');
        if (!this.systemDiagnostics.gpu || this.systemDiagnostics.gpu === '—') {
            const gpuInfo = this.detectGPUInfo();
            if (gpuInfo) {
                this.systemDiagnostics.gpu = gpuInfo;
            }
        }
        const platform = (_e = (_d = (_c = nav.userAgentData) === null || _c === void 0 ? void 0 : _c.platform) !== null && _d !== void 0 ? _d : nav.platform) !== null && _e !== void 0 ? _e : '—';
        this.systemDiagnostics.platform = platform;
        if (userAgentData) {
            this.systemDiagnostics.userAgent = `${platform}: ${userAgentData}`;
        }
        else {
            this.systemDiagnostics.userAgent = navigator.userAgent;
        }
    }
    detectGPUInfo() {
        try {
            const canvas = document.createElement('canvas');
            const gl = (canvas.getContext('webgl') || canvas.getContext('experimental-webgl'));
            if (!gl)
                return null;
            const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
            if (debugInfo) {
                const renderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);
                if (renderer) {
                    return renderer;
                }
            }
            const renderer = gl.getParameter(gl.RENDERER);
            return renderer ? String(renderer) : null;
        }
        catch (err) {
            return null;
        }
    }
    computeStoragePersistence() {
        const storage = navigator.storage;
        if (!storage || typeof storage.persisted !== 'function') {
            this.storagePersisted = null;
            return;
        }
        storage.persisted().then(result => {
            this.storagePersisted = result;
            if (this.storageStats) {
                this.storageStats.persisted = result;
            }
            this.storageDirty = true;
        }).catch(() => {
            this.storagePersisted = null;
        });
    }
    setupNetworkMonitoring() {
        this.patchFetch();
        this.patchXHR();
        this.observePerformanceEntries();
    }
    patchFetch() {
        if (this.fetchPatched || typeof window.fetch !== 'function')
            return;
        const originalFetch = window.fetch.bind(window);
        window.fetch = ((...args) => {
            this.onNetworkRequestStart();
            return originalFetch(...args).then(response => {
                this.onNetworkRequestEnd();
                return response;
            }).catch(error => {
                this.onNetworkRequestEnd();
                throw error;
            });
        });
        this.fetchPatched = true;
    }
    patchXHR() {
        if (this.xhrPatched || typeof XMLHttpRequest === 'undefined')
            return;
        const tracker = this;
        const originalSend = XMLHttpRequest.prototype.send;
        XMLHttpRequest.prototype.send = function (...args) {
            tracker.onNetworkRequestStart();
            const xhr = this;
            const cleanup = () => {
                tracker.onNetworkRequestEnd();
                xhr.removeEventListener('loadend', cleanup);
                xhr.removeEventListener('error', cleanup);
                xhr.removeEventListener('abort', cleanup);
            };
            xhr.addEventListener('loadend', cleanup);
            xhr.addEventListener('error', cleanup);
            xhr.addEventListener('abort', cleanup);
            return originalSend.apply(xhr, args);
        };
        this.xhrPatched = true;
    }
    observePerformanceEntries() {
        if (typeof PerformanceObserver === 'undefined')
            return;
        try {
            this.performanceObserver = new PerformanceObserver(list => {
                for (const entry of list.getEntries()) {
                    if (entry.entryType === 'resource') {
                        const resource = entry;
                        if (resource.initiatorType === 'fetch' || resource.initiatorType === 'xmlhttprequest' || resource.initiatorType === 'other') {
                            this.processResourceTiming(resource);
                        }
                    }
                }
            });
            this.performanceObserver.observe({ type: 'resource', buffered: true });
        }
        catch (err) {
            // ignore observer errors
        }
    }
    processResourceTiming(resource) {
        const bytes = resource.transferSize || resource.encodedBodySize || resource.decodedBodySize || 0;
        const now = performance.now();
        this.networkStats.lastTransferBytes = bytes;
        this.networkStats.lastDurationMs = resource.duration;
        this.networkStats.lastUpdated = now;
        this.recentNetworkEvents.push({ time: now, bytes });
        this.recentByteTotal += bytes;
        this.trimRecentNetworkEvents(now);
        const windowSeconds = this.recentWindowMs / 1000;
        this.networkStats.recentBytesPerSecond = this.recentByteTotal / windowSeconds;
        this.networkStats.recentRequestsPerSecond = this.recentNetworkEvents.length / windowSeconds;
        this.networkDirty = true;
    }
    trimRecentNetworkEvents(now) {
        while (this.recentNetworkEvents.length > 0 && now - this.recentNetworkEvents[0].time > this.recentWindowMs) {
            const event = this.recentNetworkEvents.shift();
            if (event) {
                this.recentByteTotal = Math.max(0, this.recentByteTotal - event.bytes);
            }
        }
        const windowSeconds = this.recentWindowMs / 1000;
        this.networkStats.recentBytesPerSecond = this.recentByteTotal / windowSeconds;
        this.networkStats.recentRequestsPerSecond = this.recentNetworkEvents.length / windowSeconds;
    }
    onNetworkRequestStart() {
        this.networkStats.activeRequests += 1;
        this.networkStats.startedRequests += 1;
        this.networkDirty = true;
    }
    onNetworkRequestEnd() {
        if (this.networkStats.activeRequests > 0) {
            this.networkStats.activeRequests -= 1;
        }
        this.networkStats.completedRequests += 1;
        this.networkDirty = true;
    }
}
export const diagnosticsService = DiagnosticsService.getInstance();
